import { CHART_INDICATOR_NAME, CALCULATION_MODE } from '../Modals/DashboardModal/constants';
import { FilterResults } from '../Modals/FilterModal/filterModalInterface';

export type HorizontalBarProps = {
    base: number
    data: {key:string, value: string}
    error: string | null
    round: number
}

export interface KeyFigureProps {
    keyFigureNumber: string | number
    previousNumber?: string
    year?: string
}

export interface KeyFigureFromApiProps {
    base: number
    data: { key: string, value: number }
    previousNumber?: string
    year?: string
    error: string | null
    round: number
    quantiles: { key: string, value: number }
    comparison?: ComparisonProps | null
}

export enum Evolution {
    POSITIVE = 'POSITIVE',
    NEGATIVE = 'NEGATIVE',
    UP = 'UP',
    DOWN = 'DOWN',
}

// What the significant gaps of a card are read against, which its legend tells.
export enum EvolutionLegendKind {
    GLOBAL = 'GLOBAL',
    PERIOD = 'PERIOD',
    TOTAL = 'TOTAL',
}

export interface ComparisonSeriesProps {
    data: { [label: string]: number }
    delta: { [label: string]: number | null }
    significance: {
        test: string | null
        confidence: number
        notTestedReason: string | null
        results: { [label: string]: { pValue: number, isSignificant: boolean, direction: string | null } | null }
    }
}

export interface ComparisonProps {
    type: string
    label: string
    relation: string
    base: number
    error: string | null
    results: { [seriesKey: string]: ComparisonSeriesProps }
}

export interface DonutProps {
    keyFigure: string | {key:string, value: number},
    base: number
    hasIndicatorKpiGroup: boolean
    labels: string[]
    datasets: Array<{
        label: string,
        data: (string | number)[],
        backgroundColor: string[],
        hoverOffset?: number
    }>
    round: number
    comparedKeyFigure?: { [label: string]: number }
    comparedValues?: { [label: string]: number }
    comparisonType?: string
    keyFigureEvolution?: Evolution | null
    valueEvolutions?: { [label: string]: Evolution | null }
}

export interface DonutFromApiProps {
    base: number
    // eslint-disable-next-line camelcase
    donut_result: { key: string, value: number }
    // eslint-disable-next-line camelcase
    has_indicator_kpi_group: boolean
    // eslint-disable-next-line camelcase
    donut_key_result: { key: string, value: number } | null
    error: string | null
    round: number
    donut_colors: { key: string, value: string }
    comparison?: ComparisonProps | null
}

export interface VerbatimProps {
    positive: string
    negative: string
}

export interface AdditionalVarsProps {
    id: number
    value: string
}

export interface ListedVerbatimProps {
    interviewId: number
    openData: string
    additionalVars?: Array<AdditionalVarsProps>
}

export type DataResult = {
    current: { res: number, base: number }
    comparate: { res: number, base: number }
    evolution: string
    type: CALCULATION_MODE
}

// Verdict of a cross table column against its TOTAL column, null on the TOTAL itself.
export type CrosstableSignificance = {
    test: string | null
    confidence: number
    notTestedReason: string | null
    result: { pValue: number, isSignificant: boolean, direction: string | null } | null
}

export type Data = {
    label: String
    modal: number
    data: DataResult
    datas: Array<Data>
    type: string
    significance?: CrosstableSignificance | null
}

export type Dataset = {
    column_id: number
    datas: Array<Data>
}
export interface CrosstableProps {
    crossing_question_label: string
    columns: Array<{ id: number, label: string }>
    rows: Array<{ id: number, label: string }>
    datasets: Array<Dataset>
    base: number
    error: string | null
    bases: Array<number>
    round: number
    computeMethod: string
}

export type IndicatorData = KeyFigureFromApiProps | DonutFromApiProps | VerbatimProps | HorizontalBarProps | ListedVerbatimProps | ChatbotProps | CrosstableProps;

export type Dashboard = {
    id: number,
    name: string,
    surveyId: number,
    rankOfIndicator: {[key: number]: number}
}

export type CreateDashboardProps = {
    surveyId: string,
    name: string,
    slug: string,
    columnsConfig: number[],
}

export type Indicator = {
    id: number
    indicatorType: string
    displayType: CHART_INDICATOR_NAME
    hasIndicatorKpiGroup?: boolean
    customItemTitle: string
    data: IndicatorData | undefined
    question: {
        id: number
        label: string
    }
    error?: string
    additionalVars?: Array<number>
    dashboardColumn: number
    rank: number
}

export interface UpdateDashboardProps {
    id?: number
    name?: string
    surveyId?: number
    rankOfIndicator?: {key: number, value:number}
    slug?: string
    customSurveyTitle?: string
    dashboardFilterConfig?: FilterResults
}

export interface DashboardFromApi {
    id: number
    name: string
    surveyId: number
    rankOfIndicator: {key: number, value:number}
    slug: string
    customSurveyTitle: string
    dashboardFilterConfig: Array<FilterFromApi>
    temporalScopesConfig: Array<TemporalScopeFromApi>
    columnsConfig: number[]
}

export interface FilterFromApi {
    filter: { key: string, value: string }
    lockedModalities: Array<{ value: string }>
    modalities: Array<{ key: string, value: string }>
}

export interface TemporalScopeFromApi {
    scope: { key: string, value: string }
    modalities: Array<{ key: string, value: string }>
    hasInitWithLastValue: boolean
}

export interface ClauseFilterProps {
    filterId: string
    modalityId: Array<string>
}

export interface ClauseFilterLabelProps {
    filterLabel: string
    modalitiesLabel: string
}

export interface ScopeProps {
    scopeId: string
    modalityId: Array<string>
}
export interface ScopeLabelProps {
    scopeId: number
    scopeLabel: string
    modalitiesLabel: string
}
export type DashboardParams = {
    surveyId: string
    dashboardId: string
}

export interface DashboardItemListFromApi {
    id: string
    name: string
}
export interface DashboardItemFromApi {
    id: number
    name: string
    slug: string
    customSurveyTitle: string
    surveyId: number
}

export interface GradientColorProps {
    startColor: string,
    endColor: string,
    minNum: number,
    maxNum: number
}

export type RenameDashboardProps = {
    customTitle: string,
}

export interface DateRangeScopeProps {
    startDate: Date,
    endDate: Date,
    key: string
}

export interface ChatProps {
    starter: boolean,
    isSummary: boolean,
    question: string
    language?: string
}

export interface ChatbotProps {
    chatId: string,
    response: string,
    responseDate: Date
}

export interface IndicatorsOrder {
    id: number,
    dashboard_column: number,
    rank: number
}
